/**
 * goalKnowledge — pure, React-free helpers for the Goal "Documents & Knowledge
 * Base" capability.
 *
 * A goal's uploaded documents live under a GOAL-SCOPED relative prefix inside
 * the team knowledge base's S3 data source, e.g. `goals/<goalId>/<file>` (the
 * backend prefixes it with the configured bucket prefix and validates it).
 * They are uploaded through the same {@link import('../../api').KnowledgeBaseApi}
 * UploadDocument op the Chat page uses, and ingested (StartIngestion) into the
 * SAME planner knowledge base — so once built, the docs are retrievable by every
 * KB surface (Ask Vector, task KB Research, Estimate with Vector), not just this
 * goal.
 *
 * {@link buildGoalQuestion} wraps a user's free-text question with the goal's
 * identity + narrative so RetrieveAndGenerate favours the goal's own documents
 * and answers in cited Markdown.
 *
 * Pure TS — no React, no api. Consumed by {@link ./goalKnowledgeStore} and
 * {@link ./useGoalKnowledge}.
 */

import { v4 as uuid } from 'uuid';
import type { Goal } from './types';

/**
 * A goal-scoped document tracked in the `goalDocuments` DocumentStore
 * collection (DDB pointer row + S3 body — like every other store). The document
 * itself lives under a goal-scoped prefix inside the team KB's S3 data source
 * ({@link goalKbObjectKey}); THIS record is the queryable pointer to it, so the
 * Goal detail page can list a goal's documents and reflect whether they have
 * been ingested into the knowledge base.
 *
 * The renderable fields are mirrored onto the pointer row's index attributes
 * (with {@link goalId} as the query key) so `listByGoal` renders WITHOUT
 * hydrating S3 bodies — mirroring the tasks/goals stores.
 */
export interface GoalDocument {
  /** Deterministic document id, e.g. `goaldoc_<uuid>`. */
  id: string;
  /** The goal this document belongs to (pointer-row index/query key). */
  goalId: string;
  /** The active workspace/room the upload was made in (attribution/scoping). */
  roomId: string;
  /** Human-readable title (defaults to the file name). */
  title: string;
  /** Original uploaded file name. */
  fileName: string;
  /** S3 object key under the goal-scoped KB prefix ({@link goalKbObjectKey}). */
  kbObjectKey: string;
  /** True once the document has been ingested (indexed) into the KB. */
  ingested: boolean;
  /** ISO-8601 timestamp the document was ingested, when {@link ingested}. */
  ingestedAt?: string;
  /** Alias of the user who uploaded the document. */
  addedBy: string;
  /** ISO-8601 timestamp the document was uploaded/recorded. */
  addedAt: string;
}

/** Deterministic document id for a new goal document. */
export function goalDocumentId(): string {
  return `goaldoc_${uuid()}`;
}

/**
 * The logical section key for the Goal knowledge base. Used BOTH as the
 * `sectionKey` when provisioning the Goal KB (StartKnowledgeBaseProvisioning)
 * AND as the bundle-key override stamped into
 * `MetaInfo.executionResourceTypeBundle.storeBundleInfoIdentifier` for goal doc
 * uploads / ingestion / KB queries — so the backend `resolveKnowledgeBase`
 * routes them to the separate Goal KB (Phase 4 separation).
 */
export const GOAL_SECTION_KEY = 'goal';

/**
 * The resolved provisioning phase for the Goal KB, derived from a Step
 * Functions execution status. RUNNING (and any non-terminal/unknown value) maps
 * to 'running'; SUCCEEDED maps to 'ready'; every other terminal status
 * (FAILED / TIMED_OUT / ABORTED) maps to 'error'.
 */
export type GoalKbProvisioningPhase = 'running' | 'ready' | 'error';

/** Terminal Step Functions execution statuses. */
const TERMINAL_STATUSES = new Set([
  'SUCCEEDED',
  'FAILED',
  'TIMED_OUT',
  'ABORTED',
]);

/** True when a Step Functions execution status is terminal (polling can stop). */
export function isTerminalExecutionStatus(status: string): boolean {
  return TERMINAL_STATUSES.has((status ?? '').trim().toUpperCase());
}

/** Maps a Step Functions execution status to the Goal KB provisioning phase. */
export function goalKbProvisioningPhase(status: string): GoalKbProvisioningPhase {
  const s = (status ?? '').trim().toUpperCase();
  if (s === 'SUCCEEDED') return 'ready';
  if (s === 'FAILED' || s === 'TIMED_OUT' || s === 'ABORTED') return 'error';
  return 'running';
}

/** Friendly labels for the per-step provisioning statuses surfaced in output. */
const STEP_LABELS: Record<string, string> = {
  PROVISIONING_STARTED: 'Starting provisioning…',
  VECTOR_INDEX_CREATED: 'Vector index created…',
  KNOWLEDGE_BASE_ACTIVE: 'Knowledge base coming online…',
  ACTIVE: 'Finalizing knowledge base…',
};

/**
 * Derives a friendly, human-readable current-step label from a DescribeExecution
 * `output` payload (a JSON string that surfaces the per-step provisioning
 * status). Tolerates a missing/blank/invalid payload and unknown step codes,
 * always returning a sensible default so the UI never shows a raw enum.
 */
export function goalKbStepLabel(output?: string): string {
  const fallback = 'Provisioning knowledge base…';
  const raw = (output ?? '').trim();
  if (!raw) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (!parsed || typeof parsed !== 'object') return fallback;

  const o = parsed as Record<string, unknown>;
  const code = [o.step, o.provisioningStatus, o.status, o.state]
    .find((v): v is string => typeof v === 'string' && v.trim() !== '')
    ?.trim()
    .toUpperCase();
  if (!code) return fallback;
  return STEP_LABELS[code] ?? fallback;
}

/** Collapses whitespace and trims; returns '' for nullish input. */
function clean(value?: string): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * The RELATIVE S3 key prefix under which a goal's documents are stored, e.g.
 * `goals/<goalId>/`. The backend prefixes this with the configured bucket
 * prefix and validates it. Kept stable so a rebuild re-indexes the same
 * location.
 */
export function goalKbPrefix(goalId: string): string {
  const safeId = goalId.trim().replace(/[^A-Za-z0-9._-]/g, '_');
  return `goals/${safeId}/`;
}

/**
 * Builds the RELATIVE S3 object key for one uploaded file under a goal's prefix,
 * e.g. `goals/<goalId>/<file>`. The backend prefixes + validates it.
 */
export function goalKbObjectKey(goalId: string, fileName: string): string {
  const safeName = fileName.trim().replace(/^\/+/, '').replace(/\s+/g, '_');
  return `${goalKbPrefix(goalId)}${safeName}`;
}

/**
 * Enriches a user's question with the goal's identity + narrative so the KB
 * retrieval is grounded in THIS goal's documents. Only present fields
 * contribute, so a sparse goal still yields a coherent prompt. Requests a cited
 * Markdown answer.
 */
export function buildGoalQuestion(
  goal: Pick<
    Goal,
    | 'title'
    | 'goalFramework'
    | 'planningPeriod'
    | 'description'
    | 'whyItMatters'
    | 'expectedOutcome'
  >,
  question: string,
): string {
  const q = clean(question);
  const title = clean(goal.title) || 'this goal';
  const framework = clean(goal.goalFramework);
  const period = clean(goal.planningPeriod);
  const scope = [framework, period].filter(Boolean).join(' ');

  const parts: string[] = [
    `Regarding the goal "${title}"${scope ? ` (${scope})` : ''}, answer the following using our team knowledge base — especially any documents uploaded for this goal.`,
    `Question: ${q}`,
  ];

  const description = clean(goal.description);
  const why = clean(goal.whyItMatters);
  const outcome = clean(goal.expectedOutcome);
  if (description) parts.push(`Goal description: ${description}.`);
  if (why) parts.push(`Why it matters: ${why}.`);
  if (outcome) parts.push(`Expected outcome: ${outcome}.`);

  parts.push('Answer in Markdown and cite the sources you used.');
  return parts.join(' ');
}
